import React, { useMemo } from 'react';
import { Card, Table, Typography } from 'antd';
import type { ExecutionCargoHydration, ExecutionEdgeHydration, ExecutionStopHydration } from './types';
import PartialExecutionCargoRow from './PartialExecutionCargoRow';
import { isLoadingStop } from './executionHelpers';
import { enrichExecutionCargo } from '../routes/execution/cargoHumanization';

const { Text } = Typography;

type Props = {
  unloadStop: ExecutionStopHydration;
  allStops: ExecutionStopHydration[];
  edges: ExecutionEdgeHydration[];
  cargo: ExecutionCargoHydration[];
};

export const SharedUnloadVisualization: React.FC<Props> = ({
  unloadStop,
  allStops,
  edges,
  cargo
}) => {
  const loadingStops = allStops.filter((s) => isLoadingStop(s.type));
  const uidToLabel = useMemo(() => {
    const map = new Map<string, string>();
    loadingStops.forEach((s, i) => map.set(s.executionStopUid, `Погрузка ${i + 1}`));
    allStops
      .filter((s) => !isLoadingStop(s.type))
      .forEach((s, i) => map.set(s.executionStopUid, `Выгрузка ${i + 1}`));
    return map;
  }, [allStops, loadingStops]);

  const incoming = edges.filter((e) => e.toExecutionStopUid === unloadStop.executionStopUid);

  const rows = incoming.map((edge) => {
    const cargoRow = cargo.find(
      (c) =>
        c.cargoId === edge.cargoId &&
        c.fromExecutionStopUid === edge.fromExecutionStopUid &&
        c.toExecutionStopUid === edge.toExecutionStopUid
    );
    return {
      key: `${edge.cargoId}-${edge.fromExecutionStopUid}`,
      cargoId: edge.cargoId,
      from: uidToLabel.get(edge.fromExecutionStopUid) ?? 'Погрузка',
      quantity: edge.quantity,
      cargoRow
    };
  });

  if (rows.length <= 1) return null;

  return (
    <Card size="small" title="Общая выгрузка — несколько источников" style={{ marginTop: 8 }}>
      <Table
        size="small"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: 'Груз', dataIndex: 'cargoId', width: 70 },
          { title: 'Из точки', dataIndex: 'from' },
          { title: 'Кол-во', dataIndex: 'quantity', width: 80 }
        ]}
        expandable={{
          expandedRowRender: (row) =>
            row.cargoRow ? (
              <PartialExecutionCargoRow
                cargo={enrichExecutionCargo([row.cargoRow], new Map())[0]}
                fromLabel={row.from}
                compact
              />
            ) : (
              <Text type="secondary">Нет данных по грузу</Text>
            )
        }}
      />
    </Card>
  );
};

export default SharedUnloadVisualization;
